import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import { useMantineTheme, useComputedColorScheme } from '@mantine/core';
import { useMove, useElementSize, useMergedRef } from '@mantine/hooks';

const ICON_LINES = [0, 1, 2] as const;

interface VerticalRangeSliderProperties {
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (v: [number, number]) => void;
  /** Color del slider. Por defecto theme.primaryColor */
  color?: string;
  /** Número de pasos mínimo entre thumbs (en múltiplos de `step`) */
  minStepsBetween?: number;
  style?: React.CSSProperties;
  orientation?: 'vertical' | 'horizontal';
  /**
   * Optional fixed size that disables the internal ResizeObserver when both
   * width and height are provided. Useful when the parent already controls the
   * slider dimensions and we want to avoid nested observers.
   */
  sizeHint?: {
    width?: number;
    height?: number;
  };
}

/**
 * Slider de rango con 3 thumbs (inferior, superior y central)
 * totalmente controlado.
 */
const VerticalRangeSlider: React.FC<VerticalRangeSliderProperties> = ({
  min,
  max,
  step = 1,
  value,
  onChange,

  minStepsBetween = 5,
  style,
  orientation = 'vertical',
  color,
  sizeHint,
}) => {
  // 0 -> lower, 1 -> upper, 2 -> center, null -> ninguno
  const [active, setActive] = useState<0 | 1 | 2 | null>(null);
  const [focusedThumb, setFocusedThumb] = useState<0 | 1 | 2 | null>(null);
  const activeReference = useRef<typeof active>(null);
  const pointerInteractionReference = useRef(false);
  const initialValueReference = useRef<[number, number]>(value);
  const rafReference = useRef<number | null>(null);
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', {
    getInitialValueInEffect: false,
  });

  /* ---------- utilidades ---------- */
  const clamp = useCallback((v: number) => Math.min(Math.max(v, min), max), [min, max]);

  const pct = useCallback((v: number) => ((v - min) / (max - min)) * 100, [min, max]);

  const snap = useCallback((v: number) => Math.round((v - min) / step) * step + min, [min, step]);

  /* ---------- update ---------- */
  const update = (ratio: number) => {
    if (rafReference.current) {
      cancelAnimationFrame(rafReference.current);
    }
    rafReference.current = requestAnimationFrame(() => {
      const raw =
        orientation === 'vertical' ? min + (1 - ratio) * (max - min) : min + ratio * (max - min);
      const snapped = snap(clamp(raw));
      const minGap = minStepsBetween * step;

      // Detectar thumb activo en el primer movimiento
      if (activeReference.current === null) {
        const distributionLow = Math.abs(snapped - value[0]);
        const distributionUp = Math.abs(snapped - value[1]);
        activeReference.current = distributionLow <= distributionUp ? 0 : 1;
        // Si pinchamos dentro del rango (no directamente en un thumb) => thumb central
        if (
          snapped > value[0] + minGap &&
          snapped < value[1] - minGap &&
          Math.min(distributionLow, distributionUp) > minGap
        ) {
          activeReference.current = 2;
        }
        setActive(activeReference.current);
        initialValueReference.current = value;
      }

      let [lower, upper] = value;

      switch (activeReference.current) {
        case 0: {
          lower = clamp(Math.min(snapped, upper - minGap));
          break;
        }
        case 1: {
          upper = clamp(Math.max(snapped, lower + minGap));
          break;
        }
        case 2: {
          const [startL, startU] = initialValueReference.current;
          const span = startU - startL;
          const centerStart = startL + span / 2;
          let delta = snapped - centerStart;
          delta = Math.max(delta, min - startL);
          delta = Math.min(delta, max - startU);
          lower = snap(startL + delta);
          upper = snap(startU + delta);
          break;
        }
        default: {
          break;
        }
      }

      if (lower !== value[0] || upper !== value[1]) {
        onChange([lower, upper]);
      }
    });
  };

  /* ---------- limpieza ---------- */
  useEffect(() => {
    return () => {
      if (rafReference.current) {
        cancelAnimationFrame(rafReference.current);
      }
    };
  }, []);

  /* ---------- gestos ---------- */
  const { ref: moveRef } = useMove(({ x, y }) => update(orientation === 'vertical' ? y : x), {
    onScrubEnd: () => {
      activeReference.current = null;
      setActive(null);
      if (pointerInteractionReference.current) {
        setFocusedThumb(null);
        pointerInteractionReference.current = false;
        if (typeof document !== 'undefined') {
          const element = document.activeElement;
          if (
            element instanceof HTMLElement &&
            containerRef.current?.contains(element)
          ) {
            element.blur();
          }
        }
      }
    },
  });

  const { ref: sizeRef, width: measuredWidth, height: measuredHeight } =
    useElementSize<HTMLDivElement>();

  const providesWidth = sizeHint?.width !== undefined;
  const providesHeight = sizeHint?.height !== undefined;
  const shouldObserveSize = !(providesWidth && providesHeight);
  const containerWidth = providesWidth ? sizeHint?.width ?? measuredWidth : measuredWidth;
  const containerHeight = providesHeight ? sizeHint?.height ?? measuredHeight : measuredHeight;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mergedRef = useMergedRef<HTMLDivElement | null>(
    moveRef,
    shouldObserveSize ? sizeRef : null,
    containerRef,
  );

  /* ---------- posiciones ---------- */
  const [pctLower, pctUpper] = useMemo(() => value.map((v) => pct(v)), [value, pct]);
  const centerPct = (pctLower + pctUpper) / 2;

  const trackSize =
    orientation === 'vertical' ? { width: 5, height: 140 } : { width: 140, height: 5 };

  const trackColor =
    colorScheme === 'dark'
      ? 'rgba(255, 255, 255, 0.22)'
      : 'rgba(12, 33, 53, 0.12)';

  const rangeColor =
    color ??
    (colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(8, 28, 60, 0.78) 0%, rgba(10, 38, 92, 0.72) 100%)'
      : 'linear-gradient(180deg, rgba(18, 40, 88, 0.32) 0%, rgba(12, 33, 74, 0.28) 100%)');

  const rangeBorderColor =
    colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(12, 33, 53, 0.3)';

  const thumbDimensions = useMemo(() => {
    if (orientation === 'vertical') {
      return { width: 24, height: 44 };
    }
    return { width: 44, height: 24 };
  }, [orientation]);

  const thumbWidth = thumbDimensions.width;
  const thumbHeight = thumbDimensions.height;

  const thumbRadius = orientation === 'vertical' ? 10 : 12;

  const thumbBackground =
    colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(46, 59, 78, 0.96) 0%, rgba(33, 44, 62, 0.94) 100%)'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 251, 255, 0.98) 100%)';

  const highlightBackground =
    colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(83, 110, 145, 0.98) 0%, rgba(64, 86, 122, 0.96) 100%)'
      : 'linear-gradient(180deg, rgba(235, 245, 255, 1) 0%, rgba(210, 230, 252, 1) 100%)';

  const thumbBorderColor =
    colorScheme === 'dark' ? 'rgba(192, 206, 226, 0.9)' : 'rgba(212, 222, 236, 0.95)';

  const thumbLineColor =
    colorScheme === 'dark' ? 'rgba(204, 220, 240, 0.9)' : 'rgba(178, 196, 220, 0.88)';

  const thumbLineAccent =
    colorScheme === 'dark' ? 'rgba(224, 236, 253, 0.95)' : 'rgba(202, 224, 250, 0.95)';

  const thumbShadow =
    colorScheme === 'dark'
      ? '0 8px 18px rgba(10, 20, 35, 0.48), 0 1px 0 rgba(255, 255, 255, 0.08)'
      : '0 10px 20px rgba(59, 87, 126, 0.14), 0 1px 0 rgba(255, 255, 255, 0.7)';

  const highlightBorderColor =
    colorScheme === 'dark' ? 'rgba(210, 224, 242, 0.95)' : 'rgba(170, 194, 224, 0.95)';

  const highlightShadow =
    colorScheme === 'dark'
      ? `${thumbShadow}, 0 0 0 2px rgba(176, 206, 244, 0.32)`
      : `${thumbShadow}, 0 0 0 2px rgba(182, 213, 255, 0.28)`;

  const highlightScale = 1.18;

  const iconContainerStyle = useMemo<React.CSSProperties>(() => {
    if (orientation === 'vertical') {
      return {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'stretch',
        gap: 3,
        width: '54%',
        height: '68%',
      };
    }
    return {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 3,
      width: '68%',
      height: '58%',
    };
  }, [orientation]);

  const iconLineBaseStyle = useMemo<React.CSSProperties>(() => {
    if (orientation === 'vertical') {
      return {
        display: 'block',
        width: '100%',
        height: 2,
        borderRadius: 1,
        background: thumbLineColor,
      };
    }
    return {
      display: 'block',
      width: 2,
      height: '100%',
      borderRadius: 1,
      background: thumbLineColor,
    };
  }, [orientation, thumbLineColor]);

  const renderThumbIcon = useCallback(
    (accent: boolean) => {
      const lineStyle = accent
        ? { ...iconLineBaseStyle, background: thumbLineAccent }
        : iconLineBaseStyle;
      return (
        <span
          style={{
            ...iconContainerStyle,
            opacity: accent ? 1 : 0.9,
          }}
          aria-hidden="true"
        >
          {ICON_LINES.map((line) => (
            <span key={line} style={lineStyle} />
          ))}
        </span>
      );
    },
    [iconContainerStyle, iconLineBaseStyle, thumbLineAccent],
  );

  const effectiveHeight =
    containerHeight || (typeof trackSize.height === 'number' ? trackSize.height : 0);
  const effectiveWidth =
    containerWidth || (typeof trackSize.width === 'number' ? trackSize.width : 0);

  const availableHeight = Math.max(effectiveHeight - thumbHeight, 0);
  const availableWidth = Math.max(effectiveWidth - thumbWidth, 0);

  const computeVerticalCenter = useCallback(
    (percent: number) => {
      const clamped = Math.max(0, Math.min(100, percent));
      if (effectiveHeight <= 0) {
        return `calc(${100 - clamped}% - ${thumbHeight / 2}px)`;
      }
      const normalized = clamped / 100;
      const center = availableHeight * (1 - normalized) + thumbHeight / 2;
      return `${center}px`;
    },
    [availableHeight, effectiveHeight, thumbHeight],
  );

  const computeHorizontalCenter = useCallback(
    (percent: number) => {
      const clamped = Math.max(0, Math.min(100, percent));
      if (effectiveWidth <= 0) {
        return `calc(${clamped}% + ${thumbWidth / 2}px)`;
      }
      const normalized = clamped / 100;
      const center = availableWidth * normalized + thumbWidth / 2;
      return `${center}px`;
    },
    [availableWidth, effectiveWidth, thumbWidth],
  );

  const centerClamped = Math.max(0, Math.min(100, centerPct));
  const centerVerticalTop = computeVerticalCenter(centerClamped);
  const centerHorizontalLeft = computeHorizontalCenter(centerClamped);
  const isCenterHighlighted = active === 2 || focusedThumb === 2;

  const centerTransform = `translate(-50%, -50%) scale(${isCenterHighlighted ? highlightScale : 1})`;

  const thumbBase: React.CSSProperties = {
    position: 'absolute',
    width: thumbWidth,
    height: thumbHeight,
    borderRadius: thumbRadius,
    background: thumbBackground,
    border: `2px solid ${thumbBorderColor}`,
    boxShadow: thumbShadow,
    touchAction: 'none',
    cursor: active === null ? 'grab' : 'grabbing',
    transition: 'transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
  };

  /* ---------- render ---------- */
  return (
    <div
      ref={mergedRef}
      style={{
        position: 'relative',
        userSelect: 'none',
        touchAction: 'none',
        boxSizing: 'border-box',
        ...trackSize,
        ...style,
      }}
    >
      {/* Track */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 4,
          background: trackColor,
        }}
      />

      {/* Rango seleccionado */}
      <div
        style={{
          position: 'absolute',
          background: rangeColor,
          borderRadius: 4,
          boxShadow: `0 0 0 1.5px ${rangeBorderColor}`,
          ...(orientation === 'vertical'
            ? {
                left: '15%',
                width: '70%',
                bottom: `${pctLower}%`,
                height: `${pctUpper - pctLower}%`,
              }
            : {
                top: '15%',
                height: '70%',
                left: `${pctLower}%`,
                width: `${pctUpper - pctLower}%`,
              }),
        }}
      />

      {/* Thumbs inferior y superior */}
      {[pctLower, pctUpper].map((p, index) => {
        const isCurrentActive = active === index;
        const isFocused = focusedThumb === index;
        const isHighlighted = isCurrentActive || isFocused;
        const clamped = Math.max(0, Math.min(100, p));
        const transform = `translate(-50%, -50%) scale(${isHighlighted ? highlightScale : 1})`;
        const currentShadow = isHighlighted ? highlightShadow : thumbShadow;
        const positionStyle =
          orientation === 'vertical'
            ? {
                left: '50%',
                top: computeVerticalCenter(clamped),
              }
            : {
                top: '50%',
                left: computeHorizontalCenter(clamped),
              };
        return (
          <div
            key={index}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={index === 0 ? value[0] : value[1]}
            aria-orientation={orientation}
            tabIndex={0}
            onMouseDown={(event) => {
              event.stopPropagation();
              activeReference.current = index as 0 | 1;
              setActive(index as 0 | 1);
              pointerInteractionReference.current = true;
            }}
            onTouchStart={(event) => {
              event.stopPropagation();
              activeReference.current = index as 0 | 1;
              setActive(index as 0 | 1);
              pointerInteractionReference.current = true;
            }}
            onFocus={() => setFocusedThumb(index as 0 | 1)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setFocusedThumb((current) => (current === index ? null : current));
              }
            }}
            style={{
              ...thumbBase,
              boxShadow: currentShadow,
              borderColor: isHighlighted ? highlightBorderColor : thumbBorderColor,
              background: isHighlighted ? highlightBackground : thumbBackground,
              ...positionStyle,
              transform,
            }}
          >
            {renderThumbIcon(isHighlighted)}
          </div>
        );
      })}

      {/* Thumb central */}
      <div
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value[0] + (value[1] - value[0]) / 2}
        aria-orientation={orientation}
        tabIndex={0}
        onMouseDown={(event) => {
          event.stopPropagation();
          activeReference.current = 2;
          setActive(2);
          initialValueReference.current = value;
          pointerInteractionReference.current = true;
        }}
        onTouchStart={(event) => {
          event.stopPropagation();
          activeReference.current = 2;
          setActive(2);
          initialValueReference.current = value;
          pointerInteractionReference.current = true;
        }}
        onFocus={() => setFocusedThumb(2)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setFocusedThumb((current) => (current === 2 ? null : current));
          }
        }}
        style={{
          ...thumbBase,
          boxShadow: isCenterHighlighted ? highlightShadow : thumbShadow,
          borderColor: isCenterHighlighted ? highlightBorderColor : thumbBorderColor,
          background: isCenterHighlighted ? highlightBackground : thumbBackground,
          ...(orientation === 'vertical'
            ? {
                left: '50%',
                top: centerVerticalTop,
              }
            : {
                top: '50%',
                left: centerHorizontalLeft,
              }),
          transform: centerTransform,
        }}
      >
        {renderThumbIcon(isCenterHighlighted)}
      </div>
    </div>
  );
};

export { VerticalRangeSlider };
